import { Transition } from "@headlessui/react";
import { MessageCircle } from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import downArrow from "src/assets/down-arrow.svg";
import starEmpty from "src/assets/starEmpty.svg";
import SuccessIcon from "src/assets/successCircle.svg";
import { Autocomplete, Button, Checkbox, Input, Modal, Spinner } from "src/components";
import VersioningCrate from "src/components/Versioning/versioningModal.component";
import { invoiceActions } from "src/context/invoice-slice";
import { RootState } from "src/context/store";
import Download from "src/features/fields/assets/Download copy.svg";
import star from "src/features/fields/assets/star.svg";
import { useEventStatus } from "src/hooks/useEventStatus";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { useToast } from "src/hooks/useToast";
import { downloadInvoice, editInvoice, fetchInvoicesData, getInvoiceActionAllowed, getInvoiceChangeValidationV1, getInvoiceDetails, getSimilarOrders } from "src/services/invoice-api";
import Book from "../../assets/book.png";
import PurpleLeftArrowIcon from "../../assets/purple-left-arrow.svg";
import { DEFAULT_TABLE_DATA, getInvoiceDetailsInfo } from "../invoice-master/invoice-helper";
import InvoiceLayout from "../invoice-master/invoice-layout.component";
import InvoiceDetailCard from "./invoice-detail.component";
import InvoiceApprovalOrderedData from "./InvoiceApprovalCards/ApprovalOrderedData";
import NetAmountCompare from "./InvoiceApprovalCards/NetAmountCompare";
import RecallInvoice from "./InvoiceApprovalCards/RecallInvoice";
import RevertInvoice from "./InvoiceApprovalCards/RevertInvoice";
import InvoiceRemarks from "./InvoiceRemarks";
import PartialIcon from "src/assets/partial.svg";
import IsDifferentOrder from "./InvoiceApprovalCards/IsDifferentOrder";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import usePermission from "src/hooks/usePermission";
import ApprovalCard from "./InvoiceApprovalCards/ApprovalCard";
import useDebounceLeading from "src/hooks/useDebounceLeading";

const subtleColors = ["#F9F9FF", "#EFF8FF", "#F3FFF4", "#FFF5F5", "#FDFCF7", "#F3F0FF", "#EFFFFA", "#FFF3F7", "#F0FBFF", "#F7F5FF"];

export const InvoiceApprovalScreen = () => {
  const { stockist_invoice_attachment_id } = useParams();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { invoiceApprovalData, InvoiceApprovalFilters, currentApprovalInvoiceIndex, currentApprovalPageIndex, invoiceApprovalList } = useSelector((state: RootState) => state.invoice);
  const [mappedData, setMappedData] = useState<any[]>([]);
  const [selectedItemDetails, setSelectedItemDetails] = useState<any>(null);
  const [tableData, setTableData] = useState(DEFAULT_TABLE_DATA);
  const { toast } = useToast();
  const [productsData, setProductsData] = useState<any[]>(invoiceApprovalData?.invoice?.invoiceProducts);
  const [orderedData, setOrderedData] = useState<any[]>([]);
  const [similarOrders, setSimilarOrders] = useState<any[]>([]);
  const [tcs, setTcs] = useState<number | any>(0);
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const navigate = useNavigate();
  const [isRemarksShown, setIsRemarksShown] = useState(false);
  const [showOrderDropdown, setShowOrderDropdown] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [VersioningModal, setVersioningModal] = useState(false);
  const [remainingQuantity, setRemainingQuantity] = useState<any[]>([]);
  const [revertInvoiceOpen, setRevertInvoiceOpen] = useState<boolean>(false);
  const [recallInvoiceOpen, setRecallInvoiceOpen] = useState<boolean>(false);
  const [openNetAmtModal, setOpenNetAmtModal] = useState<boolean>(false);
  const [entryNetAmt, setEntryNetAmt] = useState<string>("");
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [checkedRows, setCheckedRows] = useState<Set<string>>(new Set());
  const [isDifferentOrderModalOpen, setIsDifferentOrderModalOpen] = useState<boolean>(false);
  const [orderToSelect, setOrderToSelect] = useState<any[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState<boolean>(false);
  const [approvalBy, setApprovalBy] = useState<any>(null);

  // const [isAutoApproving, setIsAutoApproving] = useState<boolean>(false);

    const location = useLocation();
    const { CompanyCode } = location.state || {};

  const { hasPermission: viewRemarks } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.VIEWREMARKS.module,
    action: fineGrainedPermissionsKeys.INVOICE.VIEWREMARKS.action,
  });

  const getRowId = (item: any, index: number) => `${item.unique_key}-${index}`;

  const uniqueKeyColorMap: Record<string, string> = {};
  let colorIndex = 0;

  const company_code = invoiceApprovalData?.company_code;

  mappedData.forEach((item) => {
    if (!uniqueKeyColorMap[item.unique_key?.split("~")?.slice(0, 3)?.join("~")]) {
      uniqueKeyColorMap[item.unique_key?.split("~")?.slice(0, 3)?.join("~")] = subtleColors[colorIndex % subtleColors.length];
      colorIndex++;
    }
  });

  function getNextInvoice(invoiceData: any, currentApprovalInvoiceIndex: any) {
    // Ensure the next index is within bounds
    if (currentApprovalInvoiceIndex + 1 < invoiceData.length) {
      return invoiceData[currentApprovalInvoiceIndex + 1];
    } else {
      // No next invoice (end of the list)
      return null;
    }
  }

  const searchParams = useMemo(
    () => ({
      company_code: InvoiceApprovalFilters?.company?.map((item) => item.code)?.join(",") || "",
      division_code: InvoiceApprovalFilters?.division?.map((item) => item.company_divisioncode)?.join(",") || "",
      startDate: InvoiceApprovalFilters?.dateRange?.from,
      endDate: InvoiceApprovalFilters?.dateRange?.to,
      assignee_id: InvoiceApprovalFilters?.assignee?.map((item) => item.invoice_assignment_id)?.join(",") || "",
      city: InvoiceApprovalFilters?.city?.map((item) => item.city_name?.split("|")?.[0]?.trim())?.join(",") || "",
      district: InvoiceApprovalFilters?.district?.map((item) => item.district_name)?.join(",") || "",
      state: InvoiceApprovalFilters?.state?.map((item) => item.state_name)?.join(",") || "",
      ordering: InvoiceApprovalFilters?.sorting?.[0]?.key || "DESC",
      fuid_status_id: InvoiceApprovalFilters?.status?.map((item) => item.fuid_status_id)?.join(",") || 1,
      page_size: 10,
      page_number: 1,
      screen: "Entry",
      dateType: InvoiceApprovalFilters?.dateType?.[0]?.key || "",
    }),
    [InvoiceApprovalFilters]
  );

  const fetchMoreDataAfterInvoiceApproval = async () => {
    if (isLoading) return;
    try {
      const nextPageParams = { ...searchParams, page_number: currentApprovalPageIndex + 1 };
      const res = await fetchInvoicesData(nextPageParams);
      const newData = res?.data?.data?.invoiceData;
      if (!newData || newData.length === 0) {
        return;
      }
      dispatch(invoiceActions.setInvoiceApprovalList([...(invoiceApprovalList || []), ...newData]));
      dispatch(invoiceActions.setCurrentApprovalPageIndex(currentApprovalPageIndex + 1));
    } catch (error) {
      console.error("Error fetching more invoices", error);
    } finally {
      setIsLoading(false);
    }
  };

  const outSideClickRef = useOutsideClick(() => {
    setVersioningModal(false);
  });
  const { withEventCheck } = useEventStatus();

  const inputRef = useRef<HTMLInputElement>(null);
  const ref = useOutsideClick(() => {
    setIsRemarksShown(false);
  });

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };
  const fetchInvoiceDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getInvoiceDetails(stockist_invoice_attachment_id as string, false, CompanyCode as string);
      dispatch(invoiceActions.setInvoiceApprovalData(response?.data?.data));

      let productsDataStateTobeSaved = [];
      if (response?.data?.data?.fileStatusHdr?.fuid_status_id == 8) {
        productsDataStateTobeSaved = response?.data?.data?.invoice?.invoiceProducts?.map((item: any) => {
          const mappedQuantities = item?.orderProductMappings?.reduce(
            (acc: { quantity: number; free_qty: number }, mapping: any) => {
              acc.quantity += Number(mapping?.quantity) || 0;
              acc.free_qty += Number(mapping?.free_qty) || 0;
              return acc;
            },
            { quantity: 0, free_qty: 0 }
          );

          const unmappedQty = (Number(item?.quantity) || 0) - mappedQuantities?.quantity;
          const unmappedFreeQty = (Number(item?.free_qty) || 0) - mappedQuantities?.free_qty;

          return {
            ...item,
            unmappedQty,
            unmappedFreeQty,
            unique_key: `${item?.item_code}~${item?.batch_no}`,
            mapped: true,
            invoice_status: unmappedQty === 0 && unmappedFreeQty === 0 ? "fully_invoiced" :
              item?.quantity === unmappedQty && item?.free_qty === unmappedFreeQty ? "" : "partially_invoiced",
          };
        });
      } else {
        productsDataStateTobeSaved = response?.data?.data?.invoice?.invoiceProducts?.map((item: any) => ({
          ...item,
          unmappedQty: item?.quantity,
          unmappedFreeQty: item?.free_qty,
          unique_key: `${item?.item_code}~${item?.batch_no}`,
        }));
      }

      setProductsData(productsDataStateTobeSaved);
      setEntryNetAmt(response?.data?.data?.invoice?.net_amount);
      setOrderedData(
        response?.data?.data?.invoice?.invoiceProducts?.flatMap(
          (item: any) =>
            item?.orderProductMappings?.map((mapping: any) => ({
              ...mapping,
              item_description: item?.item_description,
              item_code: item?.item_code,
              inv_mrp: item?.inv_mrp,
              ptr: item?.ptr,
              price_identifier: item?.discount_type,
              batch_no: item?.batch_no,
            })) || []
        )
      );
      if (response?.data?.data?.invoice?.auto_approved?.length > 0) {
        setMappedData(response?.data?.data?.invoice?.auto_approved)
        toast({ title: "Auto approving", description: "Invoice products are auto approved", variant: "default" });
      }
      // if (response?.data?.data?.fileStatusHdr?.fuid_status_id !== 8) {
      //   handleAutoApprovalOfSchemeTypeInvoiceAndOrder(productsDataStateTobeSaved, response?.data?.data);
      // }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckboxChange = (e: any, item: any, index: number) => {
    const rowId = getRowId(item, index);

    setCheckedRows((prev) => {
      const newSet = new Set(prev);
      if (e.target.checked) {
        newSet.add(rowId);
      } else {
        newSet.delete(rowId);
      }
      return newSet;
    });

    if (e.target.checked) {
      setSelectedProducts((prev) => {
        const existing = prev.find((p) => p.unique_key === item.unique_key);
        if (existing) {
          return prev.map((p) =>
            p.unique_key === item.unique_key
              ? {
                ...p,
                quantity: p.quantity + item.quantity,
                free_qty: p.free_qty + item.free_qty,
                unmappedQty: p.unmappedQty + item.quantity,
                unmappedFreeQty: p.unmappedFreeQty + item.free_qty,
              }
              : p
          );
        } else {
          return [...prev, { ...item }];
        }
      });
    } else {
      // Unchecking this row — need to subtract this item's quantities
      setSelectedProducts((prev) => {
        const updated = prev.map((p) => {
          if (p.unique_key === item.unique_key) {
            return {
              ...p,
              quantity: p.quantity - item.quantity,
              free_qty: p.free_qty - item.free_qty,
              unmappedQty: p.unmappedQty - item.quantity,
              unmappedFreeQty: p.unmappedFreeQty - item.free_qty,
            };
          }
          return p;
        });

        // If quantities zeroed, remove the item
        return updated.filter((p) => p.quantity > 0 || p.free_qty > 0);
      });
    }
  };

  const calculateTotalCashDiscountPercentage = () => {
    const totalCashDiscount = calculateTotalCashDiscount();
    const totalSubtotal = calculateSubtotal();
    return (totalCashDiscount / totalSubtotal) * 100;
  };

  const calculateTotalCashDiscount = () => {
    let total = 0;
    mappedData?.forEach((item: any) => {
      const { gross_amt, discount_perc, cash_discount } = item;
      const afterItemDiscount = Number(gross_amt) - (Number(gross_amt) * Number(discount_perc)) / 100;
      const cashDiscountAmount = (afterItemDiscount * Number(cash_discount)) / 100;
      total += cashDiscountAmount;
    });
    return total;
  };

  // const subtotal = mappedData?.reduce((acc: number, item: any) => acc + Number(item?.quantity) * Number(item?.ptr), 0);
  const calculateSubtotal = () => {
    let total = 0;
    mappedData?.forEach((item: any) => {
      const { gross_amt, discount_perc } = item;
      const cashDiscountAmount = (gross_amt * Number(discount_perc)) / 100;
      total += gross_amt - cashDiscountAmount;
    });
    return total;
  };

  const calculateTotalGst = () => {
    let total = 0;
    mappedData?.forEach((item: any) => {
      const { gross_amt, p_gst_perc, discount_perc, cash_discount } = item;
      // Calculate GST on amount after discount
      let amountAfterItemDiscount = Number(gross_amt) - (Number(gross_amt) * Number(discount_perc)) / 100;
      let amountAfterCashDiscount = amountAfterItemDiscount - (amountAfterItemDiscount * Number(cash_discount)) / 100;

      const gstAmount = (amountAfterCashDiscount * Number(p_gst_perc)) / 100;
      total += gstAmount;
    });
    return total;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const amountAfterCashDiscount = subtotal - calculateTotalCashDiscount();
    const totalGst = calculateTotalGst();
    const tcsCalculatedAmount = amountAfterCashDiscount + totalGst + ((amountAfterCashDiscount + totalGst) * tcs) / 100;
    // return amountAfterCashDiscount + totalGst;
    return tcsCalculatedAmount;
  };

  const handleViewDetails = async (item: any) => {
    setSelectedOrders([]);
    setSelectedItemDetails(null);
    // setMappedData((prev) => prev.filter((mappedItem) => mappedItem.unique_key !== item.unique_key));
    try {
      const response = await getSimilarOrders({
        item_code: item?.item_code,
        inv_mrp: item?.inv_mrp,
        counter_code: invoiceApprovalData?.counter_code,
        company_stockist_code: invoiceApprovalData?.company_stockist_code,
        invoice_date: invoiceApprovalData?.invoice?.new_invoice_date,
        company_code: invoiceApprovalData?.company_code,
      });

      toast({
        title: "Data Fetched Successfully",
        description: "Data has been fetched successfully.",
      });

      setSimilarOrders(response?.data?.data);
      setSelectedItemDetails(item);

      //if unique_key is not present in remainingQuantity, then add it
      const incomingKey = item?.unique_key?.split("~")[0];

      setRemainingQuantity((prev) => {
        const existingIndex = prev.findIndex((product) => product?.unique_key === incomingKey);

        if (existingIndex !== -1) {
          // Update only the required fields
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            remaining_invoice_quantity: item?.quantity,
            remaining_invoice_free_quantity: item?.free_qty,
          };
          return updated;
        } else {
          // Add new entry if it doesn't exist
          return [
            ...prev,
            {
              unique_key: incomingKey,
              orders: response?.data?.data?.map((orderItem: any) => ({
                order_code: orderItem?.order?.order_code,
                remaining_quantity: orderItem?.remaining_quantity,
                remaining_free_quantity: orderItem?.remaining_free_quantity,
              })),
              remaining_invoice_quantity: item?.quantity,
              remaining_invoice_free_quantity: item?.free_qty,
            },
          ];
        }
      });

      requestAnimationFrame(() => {
        setTimeout(() => {
          if (inputRef.current) {
            // Store the original styles
            const originalBorder = inputRef.current.style.border || "1px solid #d1d5db"; // Default Tailwind gray-300
            const originalBoxShadow = inputRef.current.style.boxShadow || "none";

            // Focus and apply new styles
            inputRef.current.focus();
            inputRef.current.style.boxShadow = "0 0 0 2px #8051FF";

            // Add blur event listener to reset styles
            const resetStyles = () => {
              if (inputRef.current) {
                inputRef.current.style.border = originalBorder;
                inputRef.current.style.boxShadow = originalBoxShadow;
              }
            };

            // Remove any existing listener to prevent duplicates
            inputRef.current.removeEventListener("blur", resetStyles);
            // Add the blur listener
            inputRef.current.addEventListener("blur", resetStyles, { once: true });
          }
        }, 100);
      });
      setShowOrderDropdown(true);
    } catch (error: any) {
      console.log(error);
      toast({
        title: error?.response?.data?.error,
        description: error?.response?.data?.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (!selectedItemDetails) return;
    let modifiedQuantity;
    let modifiedFreeQty;
    let modifiedFixedPrice;

    let remainingQty;
    let OrdRemainingFreeQty;
    let OrdDiscountType;
    let OrdQuantity;

    let discountInv;
    let discountOrd;
    let InvQty;
    let InvFreeQty;
    let InvRate;
    let fixed_price;
    let InvDiscPerc;
    let InvDiscAmt;
    let InvDiscountType;

    let selectedItemUniqueKey = selectedItemDetails?.unique_key?.split("~")[0];

    remainingQty = remainingQuantity
      ?.find((item) => item?.unique_key === selectedItemUniqueKey)
      ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity;

    OrdRemainingFreeQty = remainingQuantity
      ?.find((item) => item?.unique_key === selectedItemUniqueKey)
      ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_free_quantity;

    InvQty = selectedItemDetails?.quantity;
    InvFreeQty = selectedItemDetails?.free_qty;
    InvRate = selectedItemDetails?.inv_rate;
    InvDiscPerc = (selectedItemDetails?.discount_amt / selectedItemDetails?.gross_amt) * 100;
    InvDiscAmt = selectedItemDetails?.discount_amt;
    InvDiscountType = selectedItemDetails?.discount_type;

    OrdDiscountType = selectedOrders?.[0]?.price_identifier;
    OrdQuantity = selectedOrders?.[0]?.quantity;

    //When invoice discount type is Discount Percent and order is of scheme then allow equivalent free qty of discount of invoiced quantity
    if ((InvDiscountType == "Discount Percent" || InvDiscountType == "Discount Amount") && InvDiscPerc !== 0
      && OrdDiscountType === 2) {
      const allowedFreeQtyRaw = (InvQty * (InvDiscPerc / 100));
      modifiedFreeQty = Math.round(allowedFreeQtyRaw);
      modifiedQuantity = InvQty - modifiedFreeQty;
    }
    //When invoice discount type is Scheme and order is of Discount percent then allow equivalent free qty of discount of ordered quantity
    else if (InvDiscountType == "Scheme" && OrdDiscountType === 1) {
      const allowedFreeQtyRaw = Math.round(OrdQuantity * (selectedOrders?.[0]?.discount_on_ptr / 100));
      modifiedFreeQty = OrdRemainingFreeQty;
      modifiedQuantity = InvQty + allowedFreeQtyRaw;
      // modifiedFreeQty = Math.round(allowedFreeQtyRaw);
      // modifiedQuantity = OrdQuantity - modifiedFreeQty;
    }
    //When invoice discount type is Fixed price and order is of Fixed Price
    else if ((InvDiscountType == "Discount Amount" ||
      InvDiscountType == "Discount Percent" || InvDiscountType == "Scheme") &&
      InvDiscPerc == 0 && InvDiscAmt == 0 && InvFreeQty == 0
      && OrdDiscountType === 3) {
      // const absDiffInPtrAndPrice = Math.abs(selectedOrders?.[0]?.ptr - selectedItemDetails?.inv_rate);
      // const allowedFreeQtyRaw = (InvQty * ((absDiffInPtrAndPrice / selectedOrders?.[0]?.ptr) * 100)) / 100;
      // modifiedFreeQty = Math.round(allowedFreeQtyRaw);
      // modifiedQuantity = InvQty - modifiedFreeQty;
      const calculatedQuantity = (remainingQty * selectedOrders?.[0]?.fixed_price) / selectedItemDetails?.inv_rate;
      modifiedQuantity = Math.round(calculatedQuantity);
      modifiedFreeQty = remainingQty - modifiedQuantity;
    }


    //When invoice discount type is Fixed price and order is of Scheme then allow equivalent free qty of discount of invoiced quantity
    else if ((InvDiscountType == "Discount Amount" ||
      InvDiscountType == "Discount Percent") &&
      InvDiscPerc == 0 && InvDiscAmt == 0
      && OrdDiscountType === 2) {
      const absDiffInPtrAndPrice = Math.abs(selectedOrders?.[0]?.ptr - selectedItemDetails?.inv_rate);
      const allowedFreeQtyRaw = (InvQty * ((absDiffInPtrAndPrice / selectedOrders?.[0]?.ptr) * 100)) / 100;
      modifiedFreeQty = Math.round(allowedFreeQtyRaw);
      modifiedQuantity = InvQty - modifiedFreeQty;
    }

    //When invoice discount type is Scheme  and order is of Fixed price then allow equivalent free qty of discount of ordered quantity
    else if (InvDiscountType == "Scheme" && OrdDiscountType === 3) {
      const absDiffInPtrAndPrice = Math.abs(selectedOrders?.[0]?.fixed_price - selectedItemDetails?.ptr);
      const allowedFreeQtyRaw = (OrdQuantity * ((absDiffInPtrAndPrice / selectedItemDetails?.ptr) * 100)) / 100;
      modifiedFreeQty = Math.round(allowedFreeQtyRaw);
      modifiedQuantity = OrdQuantity - modifiedFreeQty;
    }
    //When invoice discount type is Fixed price and order is Discount on PTR 
    else if ((InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent")
      && InvDiscPerc == 0 && InvDiscAmt == 0
      && OrdDiscountType === 1
    ) {
      const invoiceAmount = InvQty * InvRate;
      const orderAmount = ((OrdQuantity * selectedOrders?.[0]?.ptr)) - ((OrdQuantity * selectedOrders?.[0]?.ptr) * (selectedOrders?.[0]?.discount_on_ptr / 100));
      //if difference of invoice amount and order amount is less than or equal to 1 then allow the quantity of order
      if (Math.abs(invoiceAmount - orderAmount) <= 1) {
        modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
        modifiedFreeQty = InvFreeQty < OrdRemainingFreeQty ? InvFreeQty : OrdRemainingFreeQty;
      }
    }
    //When invoice discount type is Discount on PTR and order is of Fixed price 
    else if ((InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") && InvDiscPerc !== 0
      && OrdDiscountType === 3
    ) {
      const invoiceAmount = (InvQty * InvRate) - (InvQty * InvRate) * (InvDiscPerc / 100);
      const orderAmount = (OrdQuantity * selectedOrders?.[0]?.fixed_price);
      //if difference of invoice amount and order amount is less than or equal to 1 then allow the quantity of order
      if (Math.abs(invoiceAmount - orderAmount) <= 1) {
        modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
        modifiedFreeQty = InvFreeQty < OrdRemainingFreeQty ? InvFreeQty : OrdRemainingFreeQty;
      }
    }
    else {
      modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
      modifiedFreeQty = InvFreeQty < OrdRemainingFreeQty ? InvFreeQty : OrdRemainingFreeQty;
    }

    discountInv = String(((selectedItemDetails?.discount_amt / selectedItemDetails?.gross_amt) * 100)?.toFixed(2));

    discountOrd = (() => {
      switch (OrdDiscountType) {
        case 1:
          return selectedOrders?.[0]?.discount_on_ptr;
        case 3:
          return "Fixed Price : " + selectedOrders?.[0]?.fixed_price;
        default:
          return "0";
      }
    })();

    fixed_price = (() => {
      switch (OrdDiscountType) {
        case 3:
          return selectedOrders?.[0]?.fixed_price;
        default:
          return selectedOrders?.[0]?.ptr;
      }
    })();

    let modifiedDiscountPercent;

    if (InvDiscountType == "Scheme" && OrdDiscountType == 1) {
      modifiedDiscountPercent = discountOrd;
    } else {
      modifiedDiscountPercent = OrdDiscountType === 3 ? "0" : discountInv < discountOrd ? discountInv : discountOrd;
    }



    modifiedFixedPrice = Number(selectedItemDetails?.inv_rate) > Number(fixed_price) ? selectedItemDetails?.inv_rate : fixed_price;

    const newTableData = [
      {
        type: "Quantity",
        invoiceData: selectedItemDetails?.quantity,
        orderedData: String(selectedOrders?.reduce((acc: number, curr: any) => acc + Number(curr?.quantity), 0)),
        // modifiedData: selectedItemDetails?.quantity,

        modifiedData: modifiedQuantity,
        remaining_quantity: remainingQty,
        allowed_quantity: modifiedQuantity,
      },
      {
        type: "Discount %",
        invoiceData: discountInv,
        orderedData: discountOrd,
        modifiedData: modifiedDiscountPercent,
      },
      {
        type: "Cash Discount",
        invoiceData: selectedItemDetails?.p_cash_discount_perc || 0,
        orderedData: selectedOrders?.[0]?.order?.order_cash_discount_perc || 0,
        // modifiedData: selectedItemDetails?.p_cash_discount_perc || 0,
        modifiedData: selectedOrders?.[0]?.cash_discount_perc || 0,
        // modifiedData: data?.item_discount,
      },
      {
        type: "Scheme Qty",
        invoiceData: selectedItemDetails?.free_qty,
        orderedData: OrdDiscountType === 2 ? selectedOrders?.[0]?.free_quantity : "0",
        // modifiedData: selectedItemDetails?.free_qty,
        modifiedData: modifiedFreeQty,
        allowed_free_qty: modifiedFreeQty,
      },
      {
        type: "PTR",
        invoiceData: selectedItemDetails?.ptr,
        // orderedData: selectedOrders?.[0]?.ptr,
        orderedData: selectedItemDetails?.ptr,
        modifiedData: selectedItemDetails?.ptr,
      },
      {
        type: "MRP",
        invoiceData: selectedItemDetails?.inv_mrp,
        orderedData: selectedItemDetails?.inv_mrp,
        // orderedData: selectedOrders?.[0]?.mrp,
        modifiedData: selectedItemDetails?.inv_mrp,
      },
      {
        type: "Price",
        invoiceData: selectedItemDetails?.inv_rate,
        orderedData: fixed_price,
        modifiedData: modifiedFixedPrice,
      },
    ];

    setTableData(newTableData);
  }, [selectedOrders]);

  //On change of modified data
  const handleModifyData = (index: number, value: string) => {
    const updatedTableData = [...tableData];

    const currentType = tableData[index].type;
    const newValue = Number(value);

    //Scheme Validation
    if (currentType === "Scheme Qty") {
      const orderedFreeQuantity = Number(
        remainingQuantity
          ?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0])
          ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_free_quantity
      );
      const remainingInvoicedFreeQuantity = Number(
        remainingQuantity?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0])?.remaining_invoice_free_quantity
      );

      if (selectedItemDetails?.discount_type == "Scheme" && selectedOrders?.[0]?.price_identifier !== 2) {
        if (remainingInvoicedFreeQuantity < orderedFreeQuantity && newValue > remainingInvoicedFreeQuantity) {
          toast({
            description: `Modified Scheme Qty cannot be greater than remaining invoice free quantity ${remainingInvoicedFreeQuantity}`,
            variant: "destructive",
          });
        }
      }

      if (selectedItemDetails?.discount_type == "Scheme" && selectedOrders?.[0]?.price_identifier !== 1) {
        if (orderedFreeQuantity < remainingInvoicedFreeQuantity && newValue > orderedFreeQuantity) {
          toast({
            description: `Modified Scheme Qty cannot be greater than remaining order free quantity ${orderedFreeQuantity}`,
            variant: "destructive",
          });
        }
      }

      if (isNaN(newValue) || newValue < 0) {
        toast({
          title: "Invalid Scheme Quantity",
          description: "Please enter a valid positive number",
          variant: "destructive",
        });
        return;
      }
    }

    // Quantity validation
    if (currentType === "Quantity") {
      const orderedQuantity = Number(
        remainingQuantity
          ?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0])
          ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity
      );

      // const remainingInvoicedQuantity = Number(
      //   remainingQuantity?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0])?.remaining_invoice_quantity
      // );

      const remainingInvoicedQuantity = selectedItemDetails?.unmappedQty;

      if (newValue > remainingInvoicedQuantity) {
        toast({
          description: `Modified quantity cannot be greater than remaining invoice quantity ${remainingInvoicedQuantity}`,
          variant: "destructive",
        });
      }

      if (newValue > orderedQuantity) {
        toast({
          description: `Modified quantity cannot be greater than remaining order quantity ${orderedQuantity}`,
          variant: "destructive",
        });
      }

      if (isNaN(newValue) || newValue < 0) {
        toast({
          title: "Invalid Quantity",
          description: "Please enter a valid positive number",
          variant: "destructive",
        });
        return;
      }
    }

    // Discount % validation
    if (currentType === "Discount %") {
      const orderedDiscountType = Number(tableData[index].orderedData);
      if (newValue > orderedDiscountType) {
        toast({
          description: `Modified discount % cannot be greater than ordered discount % ${orderedDiscountType}`,
          variant: "destructive",
        });
      }
    }

    //Price Validation
    if (currentType === "Price") {
      const orderedPrice = Number(tableData[index].orderedData);
      const InvoicePrice = Number(tableData[index].invoiceData);
      if (orderedPrice > InvoicePrice && newValue > orderedPrice) {
        toast({
          description: `Modified Price cannot be greater than ordered price ${orderedPrice}`,
          variant: "destructive",
        });
      }
      if (InvoicePrice > orderedPrice && newValue > InvoicePrice) {
        toast({
          description: `Modified Price cannot be greater than ordered price ${InvoicePrice}`,
          variant: "destructive",
        });
      }
    }

    // Update the current modified value
    updatedTableData[index].modifiedData = value;

    // Mutual reset logic
    const discountIndex = tableData.findIndex((item) => item.type === "Discount %");
    const schemeIndex = tableData.findIndex((item) => item.type === "Scheme Qty");

    if (currentType === "Discount %" && schemeIndex !== -1) {
      updatedTableData[schemeIndex].modifiedData = "0";
    }

    if (currentType === "Scheme Qty" && discountIndex !== -1) {
      updatedTableData[discountIndex].modifiedData = "0";
    }

    setTableData(updatedTableData);
  };

  const handleSaveModifiedData = () => {
    setTcs(Number(invoiceApprovalData?.invoice?.tcs_perc));
    if (!selectedItemDetails) return;

    if (selectedOrders?.length == 0) {
      toast({ description: "Choose an order before submission", variant: "destructive" });
      return;
    }

    const getRemainingItem = () => {
      return remainingQuantity?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0]);
    };

    const showError = (message: string) => {
      toast({ description: message, variant: "destructive" });
      return true;
    };

    const hasInvalidQuantity = tableData.some((item) => {
      const modifiedValue = Number(item?.modifiedData);
      const invoiceValue = Number(item?.invoiceData);
      const orderedValue = Number(item?.orderedData);
      // const allowedQty = Number(item?.allowed_qty);
      // const allowedFreeQty = Number(item?.allowed_free_qty);
      const remainingItem = getRemainingItem();
      const remainingOrderQuantity = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity;
      // const remainingInvoiceQuantity = remainingItem?.remaining_invoice_quantity;
      const remainingInvoiceQuantity = selectedItemDetails?.unmappedQty;
      const remainingFreeQuantity = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)
        ?.remaining_free_quantity;
      const InvDiscountType = selectedItemDetails?.discount_type;
      const OrdDiscountType = selectedOrders?.[0]?.price_identifier;

      // Quantity validations
      if (item?.type === "Quantity") {
        if (modifiedValue > remainingOrderQuantity) {
          return showError("Modified quantity cannot be greater than remaining order quantity");
        }
        if (!((InvDiscountType === "Scheme") && (OrdDiscountType === 1))
          && (modifiedValue > remainingInvoiceQuantity)) {
          return showError(`Modified quantity cannot be greater than remaining invoice quantity ${remainingInvoiceQuantity}`);
        }
        if (!((InvDiscountType === "Scheme") && (OrdDiscountType === 1))
          &&
          (modifiedValue > invoiceValue)) {
          return showError("Quantity cannot be greater than invoiced quantity");
        }
        // if (modifiedValue > allowedQty) {
        //   return showError("Quantity cannot be greater than allowed quantity");
        // }
      }

      // Scheme Qty validations
      if (item?.type === "Scheme Qty") {
        if ((!((InvDiscountType === "Scheme") && (OrdDiscountType === 3)))
          && (!((InvDiscountType === "Scheme") && (OrdDiscountType === 1)))
          && (modifiedValue > remainingFreeQuantity)) {
          return showError("Modified free quantity cannot be greater than remaining free quantity");
        }

        if (!(((InvDiscountType === "Discount Percent" || InvDiscountType === "Discount Amount") && Number(selectedItemDetails?.discount_amt) === 0) && (OrdDiscountType === 2)) &&
          !(((InvDiscountType === "Discount Percent" || InvDiscountType === "Discount Amount") && Number(selectedItemDetails?.discount_amt) !== 0) && (OrdDiscountType === 2)) &&
          (modifiedValue > invoiceValue)) {
          return showError("Scheme Qty cannot be greater than invoiced quantity");
        }

        if ((!((InvDiscountType === "Scheme") && (OrdDiscountType === 3)))
          && (!((InvDiscountType === "Scheme") && (OrdDiscountType === 1)))
          && (modifiedValue > orderedValue)) {
          return showError("Scheme Qty cannot be greater than ordered quantity");
        }
      }

      //   if (modifiedValue > remainingFreeQuantity && OrdDiscountType !== 3 &&
      //     InvDiscountType === "Scheme" &&
      //     OrdDiscountType !== 1
      //   ) {
      //     return showError("Modified free quantity cannot be greater than remaining free quantity");
      //   }

      //   if ((OrdDiscountType !== 3 && InvDiscountType === "Scheme") &&
      //     (OrdDiscountType !== 2 && InvDiscountType !== "Scheme")) {
      //     if (modifiedValue > invoiceValue) {
      //       return showError("Scheme Qty cannot be greater than invoiced quantity");
      //     }
      //   }

      //   if (modifiedValue > orderedValue && OrdDiscountType !== 3 &&
      //     InvDiscountType === "Scheme" &&
      //     OrdDiscountType !== 1
      //   ) {
      //     return showError("Scheme Qty cannot be greater than ordered quantity");
      //   }
      // }

      // Price validations
      if (item?.type === "Price") {
        if (invoiceValue > orderedValue && modifiedValue > invoiceValue) {
          return showError("Price cannot be greater than invoiced price");
        }
        if (orderedValue > invoiceValue && modifiedValue > orderedValue) {
          return showError("Price cannot be greater than order price");
        }
      }

      // Discount validations
      if (item?.type === "Discount %" && modifiedValue > orderedValue) {
        return showError("Discount cannot be greater than ordered discount");
      }

      return false;
    });

    if (hasInvalidQuantity) {
      return;
    }



    let modifiedDiscountType = "";
    if (selectedItemDetails?.discount_type == "Scheme", selectedOrders?.[0]?.price_identifier === 1) {
      modifiedDiscountType = "Discount Percent";
    }
    else if (
      selectedOrders?.[0]?.price_identifier === 2 &&
      (selectedItemDetails?.discount_type == "Discount Percent" || selectedItemDetails?.discount_type == "Discount Amount")
    ) {
      modifiedDiscountType = "Scheme";
    } else if (
      selectedOrders?.[0]?.price_identifier === 3 &&
      Number(selectedItemDetails?.discount_perc) === 0 &&
      Number(selectedItemDetails?.discount_amt) === 0 &&
      Number(selectedItemDetails?.free_qty) === 0 &&
      Number(selectedItemDetails?.inv_rate) < Number(selectedItemDetails?.ptr)
    ) {
      modifiedDiscountType = "Fixed Price";
    } else {
      modifiedDiscountType = selectedItemDetails?.discount_type;
    }

    const orderCodes = selectedOrders?.map((item: any) => item?.id);

    const baseKey = `${selectedItemDetails?.item_code}~${selectedItemDetails?.batch_no}~${selectedOrders?.[0]?.id}`;
    const existingCount = mappedData.filter((item) => item.unique_key?.startsWith(baseKey)).length;
    const finalUniqueKey = `${baseKey}~${existingCount + 1}`;
    const modifiedQuantity = Number(tableData.find((item) => item.type === "Quantity")?.modifiedData) || 0;
    const modifiedFreeQty = Number(tableData.find((item) => item.type === "Scheme Qty")?.modifiedData) || 0;
    let grossAmount;
    if (
      selectedOrders?.[0]?.price_identifier === 3 &&
      Number(selectedItemDetails?.discount_perc) === 0 &&
      Number(selectedItemDetails?.discount_amt) === 0 &&
      Number(selectedItemDetails?.free_qty) === 0 &&
      Number(selectedItemDetails?.inv_rate) < Number(selectedItemDetails?.ptr)
    ) {
      grossAmount = Number(tableData.find((item) => item.type === "Price")?.modifiedData) * Number(modifiedQuantity);
    } else {
      grossAmount = Number(tableData.find((item) => item.type === "PTR")?.modifiedData) * Number(modifiedQuantity);
    }

    const modifiedItem = {
      ...selectedItemDetails,
      quantity: modifiedQuantity,
      free_qty: modifiedFreeQty,
      discount_perc: Number(tableData.find((item) => item.type === "Discount %")?.modifiedData),
      discount_type: modifiedDiscountType,
      cash_discount: tableData.find((item) => item.type === "Cash Discount")?.modifiedData,
      ptr: tableData.find((item) => item.type === "PTR")?.modifiedData,
      inv_mrp: tableData.find((item) => item.type === "MRP")?.modifiedData,
      inv_rate: tableData.find((item) => item.type === "Price")?.modifiedData,
      gross_amt: grossAmount,
      fixed_price: (selectedOrders?.[0]?.price_identifier === 3 &&
        Number(selectedItemDetails?.discount_perc) === 0 &&
        Number(selectedItemDetails?.discount_amt) === 0 &&
        Number(selectedItemDetails?.free_qty) === 0 &&
        Number(selectedItemDetails?.inv_rate) < Number(selectedItemDetails?.ptr)) ? tableData.find((item) => item.type === "Price")?.modifiedData : (() => {
          switch (selectedItemDetails?.discount_type) {
            case "Scheme":
              return Number(
                selectedItemDetails?.ptr - (selectedItemDetails?.ptr * ((selectedItemDetails?.free_qty / selectedItemDetails?.quantity) * 100)) / 100
              );
            default:
              return Number(
                selectedItemDetails?.ptr -
                (selectedItemDetails?.ptr * ((selectedItemDetails?.discount_amt / selectedItemDetails?.gross_amt) * 100)) / 100
              );
          }
        })(),
      invoice_id: invoiceApprovalData?.invoice?.invoice_id,
      company_stockist_code: String(invoiceApprovalData?.company_stockist_code),
      counter_code: invoiceApprovalData?.counter_code,
      order_code: orderCodes,
      p_cash_discount_perc: Number(tableData.find((item) => item.type === "Cash Discount")?.modifiedData),
      unique_key: finalUniqueKey,
    };

    setProductsData((prev) =>
      prev.map((item) => {
        const modifiedBaseKey = modifiedItem.unique_key?.split("~").slice(0, 2).join("~");

        if (item.unique_key !== modifiedBaseKey) return item;
        const InvDiscountType = selectedItemDetails?.discount_type;
        const OrdDiscountType = selectedOrders?.[0]?.price_identifier;

        let status = "not_invoiced";
        let unmappedQty;
        let unmappedFreeQty;

        //Inv -> Discount on PTR && Ord -> Scheme
        if ((InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent")
          && selectedItemDetails?.discount_amt !== 0
          && OrdDiscountType == 2) {
          if (modifiedQuantity + modifiedFreeQty === selectedItemDetails?.unmappedQty) {
            status = "fully_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
          } else if (modifiedQuantity + modifiedFreeQty < selectedItemDetails?.unmappedQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
          }
        }

        //Inv -> Fixed Price && Ord -> Scheme
        else if ((InvDiscountType == "Discount Amount" ||
          InvDiscountType == "Discount Percent") &&
          selectedItemDetails?.discount_amt === 0
          && OrdDiscountType === 2) {
          if (modifiedQuantity + modifiedFreeQty === selectedItemDetails?.unmappedQty) {
            status = "fully_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
          } else if (modifiedQuantity + modifiedFreeQty < selectedItemDetails?.unmappedQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
          }
        }

        //Inv -> scheme Ord -> Discount on PTR
        else if (InvDiscountType == "Scheme" && OrdDiscountType === 1) {
          // if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
          //   status = "partially_invoiced";
          //   unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
          //   unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          // }
          // else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
          //   status = "fully_invoiced";
          //   unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
          //   unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          // }
          if ((modifiedQuantity + modifiedFreeQty) === (selectedItemDetails?.unmappedQty + selectedItemDetails?.unmappedFreeQty)) {
            status = "fully_invoiced";
            unmappedQty = 0;
            unmappedFreeQty = 0;
          }
          else if ((modifiedQuantity + modifiedFreeQty) < (selectedItemDetails?.unmappedQty + selectedItemDetails?.unmappedFreeQty)) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
        }

        //Inv -> Fixed Price && Ord -> Discount on PTR
        else if ((InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent")
          && selectedItemDetails?.discount_amt == 0
          && OrdDiscountType === 1
        ) {
          if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
          else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
            status = "fully_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
        }

        //Inv -> Scheme && Ord -> Fixed Price
        else if (InvDiscountType == "Scheme" && OrdDiscountType === 3) {
          if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
          else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
            status = "fully_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
        }

        //Inv -> Discount on PTR && Ord -> Fixed Price
        else if (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent"
          && selectedItemDetails?.discount_amt !== 0
          && OrdDiscountType === 3
        ) {
          if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
          else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
            status = "fully_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
        }

        else {
          if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
            status = "partially_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
          else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
            status = "fully_invoiced";
            unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
            unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
          }
        }


        // if (modifiedQuantity == selectedItemDetails?.unmappedQty && modifiedFreeQty == selectedItemDetails?.unmappedFreeQty) {
        //   status = "fully_invoiced";
        // } else if (
        //   (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty)
        // ) {
        //   status = "partially_invoiced";
        // } else if (
        //   (modifiedQuantity + modifiedFreeQty === selectedItemDetails?.unmappedQty)
        // ) {
        //   status = "fully_invoiced";
        // }

        // if (modifiedQuantity == selectedItemDetails?.unmappedQty && modifiedFreeQty == selectedItemDetails?.unmappedFreeQty) {
        //   status = "fully_invoiced";
        // } else if (
        //   (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) &&
        //   selectedOrders?.[0]?.price_identifier !== 2 &&
        //   selectedItemDetails?.discount_type !== "Discount Amount" &&
        //   selectedOrders?.[0]?.price_identifier !== 2 &&
        //   selectedItemDetails?.discount_type !== "Discount Percent"
        // ) {
        //   status = "partially_invoiced";
        // } else if (
        //   (modifiedQuantity + modifiedFreeQty === selectedItemDetails?.unmappedQty &&
        //     selectedOrders?.[0]?.price_identifier === 2 &&
        //     selectedItemDetails?.discount_type === "Discount Amount") ||
        //   (selectedOrders?.[0]?.price_identifier === 2 && selectedItemDetails?.discount_type === "Discount Percent")
        // ) {
        //   status = "fully_invoiced";
        // }



        // if ((selectedItemDetails?.discount_type === "Discount Amount" || selectedItemDetails?.discount_type === "Discount Percent")
        //   && selectedItemDetails?.discount_amt !== 0
        //   && selectedOrders?.[0]?.price_identifier === 2) {
        //   unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
        //   unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
        // } else {
        //   unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
        //   unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
        // }

        return {
          ...item,
          mapped: true,
          merged: selectedProducts?.length > 0,
          unmappedQty,
          unmappedFreeQty,
          invoice_status: status,
        };
      })
    );

    setMappedData((prev) => [...prev, modifiedItem]);

    // Decrease the remaining quantity of the selected order
    setRemainingQuantity((prev) =>
      prev.map((item) => {
        const modifiedBaseKey = modifiedItem.unique_key?.split("~").slice(0, 2).join("~");
        const isMatchingItem = item.unique_key === modifiedBaseKey?.split("~")[0];

        if (!isMatchingItem) return item;

        return {
          ...item,
          orders: item.orders.map((order: any) => {
            if (order.order_code !== selectedOrders?.[0]?.id) return order;

            const isScheme =
              selectedItemDetails?.discount_type === "Scheme" &&
              (selectedOrders?.[0]?.price_identifier === 1 ||
                selectedOrders?.[0]?.price_identifier === 3);

            return {
              ...order,
              remaining_quantity: isScheme
                ? order.remaining_quantity - modifiedItem.quantity - modifiedItem.free_qty
                : order.remaining_quantity - modifiedItem.quantity,
              remaining_free_quantity: isScheme
                ? order.remaining_free_quantity
                : order.remaining_free_quantity - modifiedItem.free_qty,
            };
          }),
          remaining_invoice_quantity: item?.remaining_invoice_quantity - modifiedItem.quantity,
          remaining_invoice_free_quantity: item?.remaining_invoice_free_quantity - modifiedItem.free_qty,
        };
      })
    );



    setSelectedProducts([]);
    setCheckedRows(new Set());
    setSelectedItemDetails(null);
    setTableData(DEFAULT_TABLE_DATA);
    setSelectedOrders([]);
    setSimilarOrders([]);
    setOrderToSelect([]);

    toast({
      title: "Data Mapped Successfully",
      description: "Item has been added to mapped data.",
    });
  };

  const handleReset = (invoiceApprovalData: any) => {
    setSelectedOrders([]);
    const data = invoiceApprovalData?.invoice?.invoiceProducts?.map((item: any) => ({
      ...item,
      unmappedQty: item?.quantity,
      unmappedFreeQty: item?.free_qty,
      unique_key: `${item?.item_code}~${item?.batch_no}`,
    }));
    setProductsData(data);
    setRemainingQuantity([]);
    setMappedData([]);
    setTableData(DEFAULT_TABLE_DATA);
    setSelectedItemDetails(null);
    toast({
      title: "Data Reset Successfully",
      description: "All data has been reset.",
    });
  };

  const isRequestInProgress = useRef(false);
  const handleApproval = () => {
    setIsApprovalModalOpen(true);
  }
  const handleApproveInvoice = useDebounceLeading(async (isSkip: boolean) => {
    if (isRequestInProgress.current) return;
    isRequestInProgress.current = true;
    if (mappedData.length == 0) {
      toast({ description: "Please add products", variant: "default" });
      return;
    }

    setIsApproving(true);

    const nextInvoice = getNextInvoice(invoiceApprovalList, currentApprovalInvoiceIndex);
    dispatch(invoiceActions.setCurrentApprovalInvoiceIndex(currentApprovalInvoiceIndex + 1));

    await withEventCheck(
      () => { },
      `invoice_${invoiceApprovalData?.invoice?.invoice_id}`
    );
    const invoiceActionAllowedResponse = await getInvoiceActionAllowed(invoiceApprovalData?.invoice?.invoice_id,
      invoiceApprovalData?.stockist_invoice_attachment_id, "Approval", invoiceApprovalData?.company_code);
    if (!invoiceActionAllowedResponse?.data?.data?.allowed) {
      setIsApproving(false);
      toast({ description: invoiceActionAllowedResponse?.data?.data?.message, variant: "destructive" });
      return;
    }

    const currentDate = new Date(invoiceApprovalData?.invoice?.new_invoice_date);
    const currentYear = currentDate.getFullYear();
    const financialYearStart = new Date(currentDate.getMonth() >= 3 ? currentYear : currentYear - 1, 3, 1);

    const invoiceAccYear = financialYearStart.getFullYear();

    const payloadToValidateInV1 = {
      "cmpstockistcode": invoiceApprovalData?.company_stockist_code,
      "invoicenumber_searchcolumn": invoiceApprovalData?.invoice?.invoice_number || "",
      "invoice_accyear": invoiceAccYear,
      "company_code": invoiceApprovalData?.company_code
    }

    const response = await getInvoiceChangeValidationV1(payloadToValidateInV1)
    if (response?.data?.success !== "success") {
      toast({ description: response?.data?.message, variant: "destructive" });
      setIsApproving(false);
      return;
    }

    // const payload = {  
    //   data: mappedData,
    //   primary_key: invoiceApprovalData?.invoice?.invoice_id,
    //   event_type: "INVOICE_PRODUCT_EDIT",
    // };

    try {
      // const invoiceApproveResponse = await editInvoiceProduct(payload);
      // if (invoiceApproveResponse) {
      const invoiceEditPayload = {
        data: {
          invoice_id: invoiceApprovalData?.invoice?.invoice_id,
          stockist_invoice_attachment_id: Number(stockist_invoice_attachment_id),
          cash_discount_perc: Number(invoiceApprovalData?.invoice?.cash_discount_perc),
          cash_discount: String(calculateTotalCashDiscount()),
          net_amount: calculateTotal()?.toFixed(2),
          tcs_perc: Number(tcs),
          tcs_amt: ((calculateSubtotal() - (calculateSubtotal() * calculateTotalCashDiscount()) / 100 + calculateTotalGst()) * tcs) / 100,
          gst_amount: calculateTotalGst()?.toFixed(2),
          gross_amount: calculateSubtotal()?.toFixed(2),
          approval: true,
          // extra payload for backend versioining
          stockist_id: invoiceApprovalData?.stockist_id,
          invoice_number: invoiceApprovalData?.invoice?.invoice_number,
          // invoice header details sending for backend versioning
          u_code: invoiceApprovalData?.invoice?.u_code,
          new_invoice_date: invoiceApprovalData?.invoice?.new_invoice_date,
          // ...(qrIRN?.trim() !== "" && { e_inv_ref_type: qrIRN }),
          e_inv_ref_type: invoiceApprovalData?.invoice?.e_inv_ref_type,
          irn: invoiceApprovalData?.invoice?.irn,
          gst_validated: invoiceApprovalData?.invoice?.gst_validated,
          dl_validated: invoiceApprovalData?.invoice?.dl_validated,
          mci_validated: invoiceApprovalData?.invoice?.mci_validated,
          counter_gst_number: invoiceApprovalData?.counter_gst_number || "",
          counter_dl_number: invoiceApprovalData?.counter_dl_number || "",
          counter_mci_number: invoiceApprovalData?.counter_mci_number || "",
          invoice_num_modified: false,
          counter_data_validation: false,
          invoiceProduct: mappedData,
          ...(isSkip ? {} : { post_approval: approvalBy?.value }),
        },
        primary_key: invoiceApprovalData?.invoice?.invoice_id,
        event_type: "INVOICE_EDIT",
      };

      const invoiceEditResponse = await editInvoice(invoiceEditPayload, company_code);

      await withEventCheck(
        () => {
          if (!nextInvoice) {
            toast({ description: "No next invoice found", variant: "destructive" });
            navigate("/transactions/invoice/1");
            return;
          }
          navigate(`/transactions/invoice/${nextInvoice?.stockist_invoice_attachment_id}/${1}`);
          setIsApproving(false);
          setOpenNetAmtModal(false);
        },
        invoiceEditResponse?.data?.data?.products?.at(-1)?.eventStreamCreated?.streamId
      );

      // const invoiceStatusUpdateResponse = await withEventCheck(
      //   () => {
      //     return getInvoiceDetails(stockist_invoice_attachment_id as string, false, company_code as string);
      //   },
      //   invoiceEditResponse?.data?.data?.products?.at(-1)?.eventStreamCreated?.streamId
      // );
      toast({ description: "Invoice approved successfully", variant: "default" });
      // navigate("/transactions/invoice/1");
    } catch (error: any) {
      console.log(error);
      toast({ description: error?.response?.data?.message, variant: "destructive" });
    } finally {
      setIsApproving(false);
      isRequestInProgress.current = false;
    }
  }, 1000);

  const inputFieldValidationHandler = (item: any) => {
    const modifiedValue = Number(item?.modifiedData);

    const priceIdentifier = selectedOrders?.[0]?.price_identifier;
    const discountType = selectedItemDetails?.discount_type;

    if (item?.type === "Quantity") {
      const invoiceValue = Number(item?.invoiceData);
      const remainingItem = remainingQuantity?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0]);
      const remainingOrderQuantity = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity;
      // const remainingInvoiceQuantity = remainingItem?.remaining_invoice_quantity;
      const remainingInvoiceQuantity = selectedItemDetails?.unmappedQty;

      //Inv -> Scheme && Ord -> Discount on PTR
      if ((discountType === "Scheme") && (Number(modifiedValue) > Number(remainingOrderQuantity)) && priceIdentifier === 1) {
        return true;
      }

      if (!((discountType === "Scheme") && priceIdentifier === 1)
        &&
        (Number(modifiedValue) > Number(invoiceValue) || Number(modifiedValue) > Number(remainingOrderQuantity) || Number(modifiedValue) > Number(remainingInvoiceQuantity))) {
        return true;
      }
    } else if (item?.type === "Discount %") {
      const orderedValue = Number(item?.orderedData);
      if (modifiedValue > orderedValue) {
        return true;
      }
    } else if (item?.type === "Scheme Qty") {
      const invoiceValue = Number(item?.invoiceData);
      const remainingItem = remainingQuantity?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0]);
      const remainingFreeQuantity = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)
        ?.remaining_free_quantity;
      const remainingInvoiceFreeQuantity = remainingItem?.remaining_invoice_free_quantity;
      const allowedFreeQty = item?.allowed_free_qty;

      //Inv -> Discount on PTR && Ord -> Scheme
      if ((discountType === "Discount Percent" || discountType === "Discount Amount") &&
        Number(selectedItemDetails?.discount_amt) !== 0 &&
        (modifiedValue > allowedFreeQty) && priceIdentifier === 2) {
        return true;
      }

      //Inv -> Fixed && Ord -> Scheme
      if ((discountType === "Discount Percent" || discountType === "Discount Amount") &&
        Number(selectedItemDetails?.discount_amt) == 0 &&
        (modifiedValue > allowedFreeQty) && priceIdentifier === 2) {
        return true;
      }

      //Inv -> Scheme && Ord -> Discount on PTR
      if ((discountType === "Scheme") && (modifiedValue > allowedFreeQty) && priceIdentifier === 1) {
        return true;
      }

      //Inv -> Fixed && Ord -> Discount on PTR
      if ((discountType === "Discount Percent" || discountType === "Discount Amount") &&
        Number(selectedItemDetails?.discount_amt) == 0 &&
        (modifiedValue > allowedFreeQty) && priceIdentifier === 1) {
        return true;
      }

      //Inv -> Scheme && Ord -> Fixed Price
      if ((discountType === "Scheme") && (modifiedValue > allowedFreeQty) && priceIdentifier === 3) {
        return true;
      }

      //Inv -> Discount on PTR && Ord -> Scheme
      if ((discountType === "Discount Percent" || discountType === "Discount Amount") &&
        Number(selectedItemDetails?.discount_amt) !== 0 &&
        (modifiedValue > allowedFreeQty) && priceIdentifier === 3) {
        return true;
      }

      if ((!(discountType === "Scheme" && priceIdentifier === 3)) &&
        (discountType !== "Discount Percent" && discountType !== "Discount Amount") &&
        (!(discountType === "Scheme" && priceIdentifier === 1)) &&
        (modifiedValue > invoiceValue || modifiedValue > remainingFreeQuantity || modifiedValue > remainingInvoiceFreeQuantity)
      ) {
        return true;
      }
    } else if (item?.type === "Price") {
      const invoiceValue = Number(item?.invoiceData);
      const orderedValue = Number(item?.orderedData);

      if (invoiceValue > orderedValue) {
        if (modifiedValue > invoiceValue) {
          return true;
        }
      } else {
        if (modifiedValue > orderedValue) {
          return true;
        }
      }
    }

    return false;
  };

  //write the function if next invoice is not available then call fetchMoreDataAfterInvoiceEntry
  useEffect(() => {
    // Trigger fetch when the user is within 2 items of the end
    if (invoiceApprovalList?.length - currentApprovalInvoiceIndex <= 2) {
      fetchMoreDataAfterInvoiceApproval();
    }
  }, [currentApprovalInvoiceIndex, invoiceApprovalList?.length]);

  // useEffect(() => {
  //   dispatch(invoiceActions.setInvoiceApprovalData([]));
  //   fetchInvoiceDetails();
  // }, []);

  useEffect(() => {
    dispatch(invoiceActions.setInvoiceApprovalData([]));
    fetchInvoiceDetails();
  }, [stockist_invoice_attachment_id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <InvoiceLayout>
      <Fragment>
        <Transition
          as={Fragment}
          show={isRemarksShown}
          enter="transition transition ease-in-out duration-200 transform"
          enterFrom="translate-x-full"
          enterTo="translate-x-0"
          leave="transition  ease-in-out duration-200 transform"
          leaveFrom="translate-x-0"
          leaveTo="translate-x-full"
        >
          <div className="fixed right-0 inset-0 top-0 z-[500]">
            <InvoiceRemarks invoiceApprovalData={invoiceApprovalData} invoice_id={Number(invoiceApprovalData?.invoice?.invoice_id)} ref={ref} />
          </div>
        </Transition>
      </Fragment>
      <div className="mx-auto mt-10">
        <div className="flex flex-col px-[3.75rem] mt-6  pb-12 shadow-md border-b-[1px] border-b-[#E9E9E9]">
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col">
              <div
                className="flex items-center cursor-pointer font-semibold px-0 py-2 text-lg text-[#7F56D9]"
                onClick={() => navigate("/transactions/invoice/1")}
              >
                <img src={PurpleLeftArrowIcon} alt="back" className="w-3 h-3 mr-3" />
                Back to Invoices
              </div>
              <h1 className="text-[#49484C] font-bold text-[2rem]">Invoice Approval</h1>
            </div>
          </div>
          {viewRemarks && (
            <div className="absolute  mt-[1.16rem] mr-[1.16rem] top-64 right-0 cursor-pointer" onClick={() => setIsRemarksShown(true)}>
              <div className="relative">
                <MessageCircle className="text-gray-400 text-[24px]" />
              </div>
            </div>
          )}
        </div>
        {VersioningModal && (
          <div ref={outSideClickRef}>
            <VersioningCrate type="Invoice" invoiceId={invoiceApprovalData?.invoice?.invoice_id} />
          </div>
        )}
        <div className="p-5 mt-10 mx-[3.75rem] rounded-xl border border-[#d0d0d0] shadow-sm">
          <header className="w-full">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {invoiceApprovalData?.is_prioritised && (
                  <img src={star} className="p-1 mr-2 w-8 h-8 rounded-lg border border-[#d0d0d0] shadow-md" alt="star" />
                )}
                {!invoiceApprovalData?.is_prioritised && (
                  <img src={starEmpty} className="p-1 mr-2 w-8 h-8 rounded-lg border border-[#d0d0d0] shadow-md" alt="star" />
                )}
                <h1 className={"font-semibold text-lg text-[1.5rem] mr-4"}>
                  <span className="text-gray-400 text-[1.2rem]">Invoice# </span>
                  {invoiceApprovalData?.invoice?.invoice_number}
                </h1>
                <h1
                  className={`font-semibold  flex items-center text-xs h-6 px-2 rounded-full`}
                  style={{
                    backgroundColor: invoiceApprovalData?.fileStatusHdr?.color_code,
                    color: invoiceApprovalData?.fileStatusHdr?.text_color_code,
                  }}
                >
                  {invoiceApprovalData?.fileStatusHdr?.fuid_status_desc}
                  <img className="w-3 h-3 ml-1 cursor-pointer" src={downArrow} alt="down-arrow" />
                </h1>
              </div>
              <div className="flex gap-x-1.5">
                <Button
                  onClick={() => {
                    if (!invoiceApprovalData?.file_path) {
                      toast({ description: "Invoice not found", variant: "destructive" });
                      return;
                    }
                    downloadInvoice(invoiceApprovalData?.file_path, company_code as string).then((res: any) => {
                      if (res?.data?.success && res?.data?.data) {
                        window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
                      } else {
                        console.error("Failed to download the invoice.");
                      }
                    });
                  }}
                  className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                >
                  <img src={Download} className="w-4 h-4 mr-1" alt="save-file" />
                  Invoice{" "}
                </Button>
                <Button
                  onClick={() => {
                    if (!invoiceApprovalData?.einv_attachment_path) {
                      toast({ description: "E-Invoice not found", variant: "destructive" });
                      return;
                    }
                    downloadInvoice(invoiceApprovalData?.einv_attachment_path, company_code as string).then((res: any) => {
                      if (res?.data?.success && res?.data?.data) {
                        window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
                      } else {
                        console.error("Failed to download the E-Invoice.");
                      }
                    });
                  }}
                  className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                >
                  <img src={Download} className="w-4 h-4 mr-1" alt="send" />
                  E-Invoice{" "}
                </Button>
                {invoiceApprovalData?.fileStatusHdr?.fuid_status_id !== 8 && (
                  <Button
                    disabled={invoiceApprovalData?.fileStatusHdr?.fuid_status_id === 8}
                    className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                    onClick={() => setRevertInvoiceOpen(true)}
                    module={fineGrainedPermissionsKeys.INVOICE.REVERTINVOICE.module}
                    action={fineGrainedPermissionsKeys.INVOICE.REVERTINVOICE.action}
                  >
                    Revert Invoice
                  </Button>
                )}
                {invoiceApprovalData?.fileStatusHdr?.fuid_status_id == 8 && (
                  <Button
                    className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                    onClick={() => setRecallInvoiceOpen(true)}
                    module={fineGrainedPermissionsKeys.INVOICE.REMOVEINVOICE.module}
                    action={fineGrainedPermissionsKeys.INVOICE.REMOVEINVOICE.action}
                  >
                    Remove Invoice
                  </Button>
                )}
                {/* <Button className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600">
                  <img src={cross} className="w-4 h-4 mr-1 invert" alt="cross" />
                  Clear{" "}
                </Button> */}
              </div>
            </div>
          </header>

          <ul className="mt-5">
            <li className="my-5 mb-10 rounded-lg">
              <InvoiceDetailCard
                isExpandedInitial={true}
                isExpandable={false}
                status={invoiceApprovalData?.fileStatusHdr?.fuid_status_desc}
                checkDataType={false}
                data={getInvoiceDetailsInfo(invoiceApprovalData)}
                viewDetails={false}
                invoiceData={invoiceApprovalData}
                showHeader={false}
                fetchInvoiceDetails={fetchInvoiceDetails}
                tab={1}
              />
            </li>
          </ul>

          {(
            <div className="px-4 mt-10 mb-10 rounded-lg">
              <div className="flex items-center justify-between pb-5 border-b-2 border-gray-200">
                <p className="mb-2 text-lg font-semibold">Invoice Details</p>
              </div>

              <table className="min-w-full mt-5 border border-gray-200">
                <thead className="w-full bg-gray-100">
                  <tr>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">S.No</th> {/* M10-9670 */}
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Select</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Product Type</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Item code</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Item description</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. MRP</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. PTR</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Price</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. Qty.</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Gross Amt.</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. Scheme</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Discount %</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Discount Amount</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Scheme Qty.</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">CD %</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">GST % </th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Batch</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Order Map</th>
                    <th className="px-1 py-2 text-sm text-left text-gray-600">Mapped</th>
                  </tr>
                </thead>
                <tbody>
                  {productsData?.map((item: any, index: any) => (
                    <tr key={index} className={`${index % 2 === 0 ? "bg-gray-50 text-sm" : "text-sm"} ${item?.is_item_unknown ? "bg-[#F0F7FF]" : ""}`}>
                      <td className="px-1 py-2 border-t">{index + 1}</td>    {/* M10-9670 */}
                      {productsData?.filter((p) => p.unique_key === item.unique_key).length > 1 ? (
                        <td className="px-4 py-2 border-t">
                          <Checkbox checked={checkedRows.has(getRowId(item, index))} onChange={(e: any) => handleCheckboxChange(e, item, index)} />
                        </td>
                      ) : (
                        <td className="px-4 py-2 border-t"></td>
                      )}
                      <td className="px-1 py-2 border-t">{item?.is_item_unknown ? "UO" : "CP/OP"}</td>
                      <td className="px-1 py-2 border-t">{item?.item_code}</td>
                      <td className="px-1 py-2 border-t">{item?.item_description}</td>
                      <td className="px-1 py-2 border-t">{item?.inv_mrp}</td>
                      <td className="px-1 py-2 border-t">{item?.ptr}</td>
                      <td className="px-1 py-2 border-t">{item?.inv_rate}</td>
                      <td className="px-1 py-2 border-t">{item?.quantity}</td>
                      <td className="px-1 py-2 border-t">{item?.gross_amt}</td>
                      <td className="px-1 py-2 border-t">{item?.discount_type}</td>
                      <td className="px-1 py-2 border-t">{((item?.discount_amt / item?.gross_amt) * 100).toFixed(2)}</td>
                      <td className="px-1 py-2 border-t">{item?.discount_amt}</td>
                      <td className="px-1 py-2 border-t">{item?.free_qty}</td>
                      <td className="px-1 py-2 border-t">{item?.p_cash_discount_perc}</td>
                      <td className="px-1 py-2 border-t">{item?.p_gst_perc}</td>
                      <td className="px-1 py-2 border-t">{item?.batch_no}</td>
                      <td className="px-1 py-2 border-t">
                        <Button
                          disabled={invoiceApprovalData?.fileStatusHdr?.fuid_status_id === 8}
                          onClick={() => handleViewDetails(selectedProducts.length > 0 ? selectedProducts?.[0] : item)}
                          className="w-fit h-8 font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0]"
                        >
                          View
                        </Button>
                      </td>
                      {/* <td className="px-4 py-2 border-t">
                      <Button onClick={() => handleProductDelete(item)}>
                        <img src={DeleteIcon} alt="delete" className="w-4 h-4" />
                      </Button>
                    </td> */}
                      {
                        <td className="px-4 py-2 border-t">
                          {item?.mapped && item?.invoice_status === "fully_invoiced" && (
                            <Button>
                              <img src={SuccessIcon} alt="counter_details_check" className="" />
                            </Button>
                          )}
                          {item?.mapped && item?.invoice_status === "partially_invoiced" && (
                            <Button>
                              <img src={PartialIcon} alt="counter_details_check" className="" />
                            </Button>
                          )}
                        </td>
                      }
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {orderedData?.length === 0 && (
            <div className="mt-10 border border-[#d0d0d0] p-5 rounded-xl mx-4">
              <div className="flex items-center justify-between">
                <p className="text-lg font-semibold">{selectedItemDetails?.item_description || "Select an Item"}</p>
                <div className="flex items-center gap-2">
                  <div className="w-[200px]">
                    <Autocomplete
                      openDropdownOnMount={showOrderDropdown}
                      inputClassName="w-full"
                      placeholder="Choose an order"
                      suggestionList={similarOrders?.map((item: any) => ({
                        id: item?.order?.order_code,
                        ...item,
                      }))}
                      currentSelected={selectedOrders?.[0]?.id || ""}
                      defaultValue={selectedOrders?.[0]?.id || ""}
                      uniqueKey="id"
                      displayKey="id"
                      onSelectionChange={(value: any) => {
                        const differentOrder = invoiceApprovalData?.orderCodes?.some((order: any) => order?.order_code !== value?.id);
                        if (differentOrder) {
                          setIsDifferentOrderModalOpen(true);
                          setOrderToSelect([value]);
                        } else {
                          setSelectedOrders([value]);
                          setShowOrderDropdown(false);
                        }
                      }}
                      inputRef={inputRef}
                    />
                  </div>
                  <div className="p-2 bg-[#F1F1F1] border-2 border-gray-200 rounded-md cursor-pointer" onClick={handleToggle}>
                    <img className={`transition-transform  duration-300 ${isExpanded ? "rotate-180" : ""}`} src={downArrow} alt="down-arrow" />
                  </div>
                </div>
              </div>
              <div
                className={`transition-all items-center duration-500 ease-in-out ${isExpanded ? "opacity-100 max-h-[1000px]" : "max-h-0 opacity-0"}`}
                style={{ transitionProperty: "max-height, opacity" }}
              >
                <table className="min-w-full mt-5 border border-gray-200">
                  <thead className="w-full bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-sm font-normal text-left text-gray-600"></th>
                      <th className="px-4 py-2 text-sm font-normal text-left text-gray-600">Invoice Data</th>
                      <th className="px-4 py-2 text-sm font-normal text-left text-gray-600">
                        Ordered Data - <span className="text-[#8051FF]">{selectedOrders?.[0]?.id}</span>
                      </th>
                      <th className="px-4 py-2 text-sm font-normal text-left text-gray-600">Modified Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((item, index) => (
                      <tr key={index} className="h-12 text-sm">
                        <td className="px-4 py-2 text-gray-500 border-t">{item?.type}</td>
                        <td className="px-4 py-2 border-t">{Number(item?.invoiceData)}</td>
                        <td className="px-4 py-2 border-t">
                          {item?.orderedData}{" "}
                          {selectedOrders?.length > 0 && item?.type === "Quantity" && (
                            <span className="text-[#8051FF]">
                              (Remaining Qty:{" "}
                              {
                                remainingQuantity
                                  ?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0])
                                  ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity
                              }
                              )
                            </span>
                          )}
                          {selectedOrders?.length > 0 && item?.type === "Scheme Qty" && (
                            <span className="text-[#8051FF]">
                              (Remaining Free Qty:{" "}
                              {remainingQuantity
                                ?.find((item) => item?.unique_key === selectedItemDetails?.unique_key?.split("~")[0])
                                ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_free_quantity || 0}
                              )
                            </span>
                          )}
                        </td>
                        <td className="flex gap-2 px-4 py-2 border-t">
                          <Input
                            disabled={item.type === "MRP" || item.type === "PTR"}
                            type="number"
                            placeholder="0"
                            enableArrows={false}
                            value={Number(item?.modifiedData)}
                            onChange={(e) => {
                              handleModifyData(index, e.target.value);
                            }}
                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                            className="w-full px-2 py-1 border rounded-md border-1-black"
                            isError={inputFieldValidationHandler(item)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                {selectedItemDetails && (
                  <Button onClick={handleSaveModifiedData} className="mt-4 h-9 font-normal text-white bg-[#8051FF] shadow-sm">
                    Save Modified Data
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {orderedData?.length === 0 && (
          <div className="mt-10 border mx-[3.75rem] border-[#d0d0d0] p-5 rounded-xl px-10">
            <div
              onClick={() => {
                setVersioningModal((prev) => !prev);
              }}
              className="p-0 py-2 w-[5%] h-auto border-l-2 border-b-2 absolute top-[175px] right-[0px] -mt-12"
            >
              <img onClick={() => { }} src={Book} alt="React Logo" className="h-10 w-14" />
            </div>

            <div className="flex items-center justify-between pb-5">
              <p className="text-lg font-semibold">Final Mapped Data</p>
              {/* <Button className="w-10 h-10 bg-[#F1F1F1] border border-1-black" onClick={handleToggle}>
            <img className={`transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} src={downArrow} alt="down-arrow" />
          </Button> */}
              <Button
                disabled={invoiceApprovalData?.fileStatusHdr?.fuid_status_id === 8}
                className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                onClick={() => handleReset(invoiceApprovalData)}
              >
                Reset
              </Button>
            </div>

            <table className="min-w-full border border-gray-200">
              <thead className="w-full bg-gray-100">
                <tr>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">S.No</th>    {/* M10-9670 */}
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Item Code</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Order Code</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Item description</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. MRP</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. PTR</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Price</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. Qty.</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Gross Amt.</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. Scheme</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Discount %</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Cash Discount %</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Scheme Qty.</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">GST % </th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Batch</th>
                  <th className="px-1 py-2 text-sm text-left text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {mappedData?.map((item: any, index: any) => (
                  <tr
                    key={index}
                    className={`text-sm h-12`}
                    style={{ backgroundColor: uniqueKeyColorMap[item.unique_key?.split("~")?.slice(0, 3)?.join("~")] }}
                  >
                    <td className="px-1 py-2 border-t">{index + 1}</td>    {/* M10-9670 */}
                    <td className="px-1 py-2 border-t">{item?.item_code}</td>
                    <td className="px-1 py-2 border-t">{item?.order_code}</td>
                    <td className="px-1 py-2 border-t">{item?.item_description}</td>
                    <td className="px-1 py-2 border-t">{item?.inv_mrp}</td>
                    <td className="px-1 py-2 border-t">{item?.ptr}</td>
                    <td className="px-1 py-2 border-t">{item?.inv_rate}</td>
                    <td className="px-1 py-2 border-t">{item?.quantity}</td>
                    <td className="px-1 py-2 border-t">{Number(item?.inv_rate * item?.quantity).toFixed(2)}</td>
                    <td className="px-1 py-2 border-t">{item?.discount_type}</td>
                    <td className="px-1 py-2 border-t">{Number(item?.discount_perc).toFixed(2)}</td>
                    <td className="px-1 py-2 border-t">{item?.cash_discount}</td>
                    <td className="px-1 py-2 border-t">{item?.free_qty}</td>
                    <td className="px-1 py-2 border-t">{item?.p_gst_perc || 0}</td>
                    <td className="px-1 py-2 border-t">{item?.batch_no}</td>
                    <td className="px-1 py-2 border-t">
                      <Button
                        className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                        onClick={() => {
                          const modifiedBaseKey = item.unique_key;
                          // Filter the mappedData to exclude the current item
                          const filteredMappedData = mappedData.filter((product: any) => product?.unique_key !== item?.unique_key);
                          // Check if another item with same modifiedBaseKey exists
                          const hasSameKeyInMapped = filteredMappedData.some((product: any) => product?.unique_key === modifiedBaseKey);
                          // Update productsData only if no same key exists in remaining mappedData

                          if (!hasSameKeyInMapped) {
                            setProductsData((prev) =>
                              prev.map((product) => {
                                if (product?.unique_key?.split("~")[0] !== modifiedBaseKey?.split("~")[0]) return product;
                                const updatedUnmappedQty = product?.merged
                                  ? Number(product?.quantity)
                                  : Number(product?.unmappedQty) + Number(item?.quantity);
                                const updatedUnmappedFreeQty = product?.merged
                                  ? Number(product?.free_qty)
                                  : Number(product?.unmappedFreeQty) + Number(item?.free_qty);

                                let status = "fully_invoiced";

                                if (updatedUnmappedQty === Number(product.quantity) && updatedUnmappedFreeQty === Number(product.free_qty)) {
                                  status = "not_invoiced";
                                } else if (updatedUnmappedQty < product.quantity || updatedUnmappedFreeQty < product.free_qty) {
                                  status = "partially_invoiced";
                                }

                                return {
                                  ...product,
                                  mapped: status === "not_invoiced" ? false : true,
                                  unmappedQty: updatedUnmappedQty,
                                  unmappedFreeQty: updatedUnmappedFreeQty,
                                  invoice_status: status,
                                };
                              })
                            );
                          }

                          // Remove the item from mappedData
                          setMappedData((prev: any[]) => prev?.filter((product: any) => product?.unique_key !== item?.unique_key));
                          // Update remaining quantity
                          setRemainingQuantity((prev: any[]) =>
                            prev.map((product: any) => {
                              const modifiedBaseKey = item.unique_key?.split("~").slice(0, 2).join("~");

                              return product?.unique_key === modifiedBaseKey?.split("~")[0]
                                ? {
                                  ...product,

                                  orders:
                                    product?.orders?.map((order: any) =>
                                      order?.order_code === item?.order_code?.[0]
                                        ? {
                                          ...order,
                                          remaining_quantity: order?.remaining_quantity + item?.quantity,
                                          remaining_free_quantity: order?.remaining_free_quantity + item?.free_qty,
                                        }
                                        : order
                                    ) ?? [],
                                  remaining_invoice_quantity: product?.remaining_invoice_quantity + item?.quantity,
                                  remaining_invoice_free_quantity: product?.remaining_invoice_free_quantity + item?.free_ty,
                                }
                                : product;
                            })
                          );
                        }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {orderedData?.length > 0 && <InvoiceApprovalOrderedData orderData={orderedData} />}
        <div className="p-6 mt-10 rounded-xl border border-[#d0d0d0] w-[360px] shadow-sm mx-[3.75rem]">
          <p className="mb-2 text-lg font-semibold">Calculations</p>
          <div className="flex flex-col pb-10 gap-y-1">
            <p className="flex justify-between py-2 text-sm">
              Subtotal{" "}
              <span className="">
                ₹ {orderedData?.length > 0 ? invoiceApprovalData?.invoice?.gross_amount : Number(calculateSubtotal().toFixed(2))}
              </span>
            </p>
            <p className="flex justify-between py-2 text-sm">
              <span className="mr-2">Cash Discount %</span>
              <div className="">
                <Input
                  type="number"
                  enableArrows={false}
                  showIcon={false}
                  disabled={true}
                  placeholder="0"
                  value={
                    orderedData?.length > 0 ? invoiceApprovalData?.invoice?.cash_discount_perc : calculateTotalCashDiscountPercentage()?.toFixed(2)
                  }
                  className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                />
              </div>
            </p>
            <p className="flex justify-between py-2 text-sm">
              <span className="mr-2">Cash Discount Amount</span>
              <div className="">
                <Input
                  type="number"
                  enableArrows={false}
                  showIcon={false}
                  disabled={true}
                  value={orderedData?.length > 0 ? invoiceApprovalData?.invoice?.cash_discount : calculateTotalCashDiscount()?.toFixed(2)}
                  className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                />
              </div>
            </p>
            <p className="flex justify-between py-2 text-sm">
              GST Amount
              <span className="font-semibold">
                ₹{orderedData?.length > 0 ? invoiceApprovalData?.invoice?.gst_amount : calculateTotalGst()?.toFixed(2)}
              </span>
            </p>
            <p className="flex justify-between py-2 text-sm">
              <span className="mr-2">TCS %</span>
              <div className="">
                <Input
                  enableArrows={false}
                  showIcon={false}
                  disabled={invoiceApprovalData?.fileStatusHdr?.fuid_status_id === 8}
                  type="number"
                  placeholder="TCS"
                  value={orderedData?.length > 0 ? invoiceApprovalData?.invoice?.tcs_perc : tcs}
                  // value={Number(invoiceApprovalData?.invoice?.tcs_perc)}
                  onChange={(e: any) => setTcs(e?.target?.value)}
                  className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                />{" "}
              </div>
            </p>
            <p className="flex justify-between py-2 text-sm">
              Total (as calculated){" "}
              <span className="font-semibold">
                ₹ {orderedData?.length > 0 ? invoiceApprovalData?.invoice?.net_amount : calculateTotal().toFixed(2)}
              </span>
            </p>
            {/* <p className="flex justify-between py-2 text-sm">
              Net Inv. Amt. <span className="">₹ {calculateSubtotal().toFixed(2)}</span>
            </p> */}
            <Button
              disabled={invoiceApprovalData?.fileStatusHdr?.fuid_status_id === 8 || isRequestInProgress.current}
              loaderSize="small"
              isLoading={isApproving}
              className="w-full h-9 mt-5 font-normal text-white bg-[#8051FF] shadow-sm"
              onClick={() => {
                if (mappedData?.length == 0) {
                  toast({ description: "Please add products before approval", variant: "destructive" });
                  return;
                }
                if (Math.abs(Number(entryNetAmt) - Number(calculateTotal())) > 10) {
                  setOpenNetAmtModal(true);
                } else {
                  handleApproval();
                }
              }}
              module={fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].module}
              action={fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].action}
            >
              Approve Invoice
            </Button>
          </div>
        </div>
      </div>
      <Modal isOpen={revertInvoiceOpen} isHeader={false} onCloseModal={() => setRevertInvoiceOpen(false)}>
        <RevertInvoice
          onSuccess={() => {
            navigate("/transactions/invoice/1");
          }}
          onCloseModal={() => setRevertInvoiceOpen(false)}
        />
      </Modal>
      <Modal isOpen={recallInvoiceOpen} isHeader={false} onCloseModal={() => setRecallInvoiceOpen(false)}>
        <RecallInvoice
          onSuccess={() => {
            navigate("/transactions/invoice/1");
          }}
          onCloseModal={() => setRecallInvoiceOpen(false)}
        />
      </Modal>
      <Modal isOpen={openNetAmtModal} isHeader={false} onCloseModal={() => setOpenNetAmtModal(false)} title="Escalate Invoice">
        <NetAmountCompare
          handleApproveInvoice={handleApproval}
          calculatedTotal={calculateTotal().toFixed(2)}
          entryNetAmount={entryNetAmt}
          onCloseModal={() => setOpenNetAmtModal(false)}
          isLoading={isApproving}
        />
      </Modal>
      <Modal isOpen={isDifferentOrderModalOpen} isHeader={false} onCloseModal={() => setIsDifferentOrderModalOpen(false)} title="Is Different Order">
        <IsDifferentOrder
          handleApproveInvoice={() => {
            setSelectedOrders(orderToSelect);
            setShowOrderDropdown(false);
            setIsDifferentOrderModalOpen(false);
          }}
          onCloseModal={() => {
            setOrderToSelect([]);
            setIsDifferentOrderModalOpen(false);
          }}
          invoiceOrderCode={invoiceApprovalData?.orderCodes?.map((order: any) => order?.order_code)?.join(",")}
          selectedOrderCode={orderToSelect?.[0]?.id}
        />
      </Modal>
      <Modal isOpen={isApprovalModalOpen} isHeader={false} onCloseModal={() => setIsApprovalModalOpen(false)} title="Invoice Approval">
        <ApprovalCard isApproving={isApproving} onSuccess={(isSkip: boolean) => {
          handleApproveInvoice(isSkip);
          setIsApprovalModalOpen(false);
        }} onCloseModal={() => {
          setIsApprovalModalOpen(false);
          setApprovalBy(null);
        }} approvalBy={approvalBy} setApprovalBy={setApprovalBy} />
      </Modal>
    </InvoiceLayout>
  );
};
